/**
 * Importação da precificação pela planilha de preço da empresa (D116): abas COTAÇÃO e PREÇO SINTÉTICO.
 * Para cada item: acha ou cadastra o produto (pelo COD legado ou pela descrição) e o fornecedor (pelo nome),
 * registra a cotação com a data do arquivo e preenche o rascunho do item. O preço é o do motor do sistema,
 * com os parâmetros vigentes; da planilha vem só a margem, quando difere da herdada. A tela mostra o preço
 * da planilha ao lado para conferência.
 */
import { useMemo, useState } from "react";
import { useNotify } from "react-admin";
import { lerPrecificacaoPlanilha, type LinhaPrecoPlanilha } from "@sobral/dominio";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import TableViewIcon from "@mui/icons-material/TableViewOutlined";
import { useCelular } from "../componentes";
import { formatarData, formatarMoeda, formatarPercentual, normalizarBusca } from "../lib/formato";
import { supabase } from "../lib/supabase";
import { lerPlanilha, type ItemLicitacao } from "./licitacao-itens";
import {
  RASCUNHO_VAZIO,
  buscarProdutos,
  calcularItem,
  embalagemDaUnidade,
  gravarRascunho,
  palavrasChave,
  type ContextoFiscal,
  type Embalagem,
  type LicitacaoPreco,
  type Rascunho,
} from "./licitacao-precificacao";

interface ProdutoAchado {
  id: string;
  codigo: string;
  descricao: string;
  informatica: boolean | null;
  material_escolar: boolean | null;
}

interface FornecedorAchado {
  id: string;
  nome: string;
  uf: string;
}

/** Uma linha da planilha já cruzada com o sistema. */
interface Resolvido {
  linha: LinhaPrecoPlanilha;
  item: ItemLicitacao | null;
  /** Produto escolhido; nulo = cadastrar um novo com os dados da planilha. */
  produto: ProdutoAchado | null;
  /** Como o produto foi achado (para a tela). */
  achadoPor: "cod" | "descricao" | null;
  sugestoes: ProdutoAchado[];
  /** Nulo = cadastrar o fornecedor com o nome de fornecedorNovo. */
  fornecedor: FornecedorAchado | null;
  /** Nome do cadastro novo (o da planilha). */
  fornecedorNovo: string;
  /** Alíquota interestadual da NF (só compra de fora do estado). */
  aliquota: number | null;
  incluir: boolean;
}

/** Frete do produto (compra, D96): a planilha não informa; o usuário escolhe uma vez para todos os itens. */
type Frete = "incluso" | "depois";

const COLUNAS_PRODUTO = "id, codigo, descricao, informatica, material_escolar";
const semCuringa = (t: string) => t.replace(/[\\%_]/g, (c) => `\\${c}`);

/** Unidade do item do edital → sigla cadastrada ("UND" e "Unidade" → UN). */
function unidadeDoProduto(unidadeItem: string | null, unidades: { sigla: string; descricao: string }[]): string | null {
  const alvo = normalizarBusca(unidadeItem ?? "").trim();
  if (!alvo) return null;
  const achada = unidades.find((u) => normalizarBusca(u.sigla) === alvo || normalizarBusca(u.descricao) === alvo);
  if (achada) return achada.sigla;
  if (["und", "unid", "unidade"].includes(alvo)) return unidades.find((u) => u.sigla === "UN")?.sigla ?? null;
  return null;
}

async function acharProduto(linha: LinhaPrecoPlanilha): Promise<{ produto: ProdutoAchado | null; achadoPor: Resolvido["achadoPor"]; sugestoes: ProdutoAchado[] }> {
  if (linha.cod) {
    const { data } = await supabase.from("produto_embalagem").select(`produto:produto_id(${COLUNAS_PRODUTO})`).eq("codigo_legado", linha.cod).maybeSingle();
    const produto = (data as { produto: ProdutoAchado | null } | null)?.produto ?? null;
    if (produto) return { produto, achadoPor: "cod", sugestoes: [] };
  }
  const { data: iguais } = await supabase.from("produto").select(COLUNAS_PRODUTO).eq("ativo", true).ilike("descricao", semCuringa(linha.descricao)).limit(1);
  const igual = ((iguais ?? []) as ProdutoAchado[])[0];
  if (igual) return { produto: igual, achadoPor: "descricao", sugestoes: [] };
  const parecidos = await buscarProdutos(palavrasChave(linha.descricao).join(" "));
  return {
    produto: null,
    achadoPor: null,
    sugestoes: parecidos.slice(0, 5).map((p) => ({ id: p.id, codigo: p.codigo, descricao: p.descricao, informatica: p.informatica, material_escolar: null })),
  };
}

/**
 * Fornecedor pelo nome e pela UF de origem: a mesma empresa pode ter filiais em estados diferentes
 * (FUJIOKA GO e DF), e a UF muda o ICMS. Nome igual vence; senão, nome que começa igual na mesma UF.
 */
async function acharFornecedor(nome: string, uf: string): Promise<{ achado: FornecedorAchado | null; novo: string }> {
  if (!nome) return { achado: null, novo: nome };
  const padrao = semCuringa(nome);
  const { data: iguais } = await supabase.from("fornecedor").select("id, nome, uf").ilike("nome", padrao).eq("uf", uf).limit(1);
  const igual = ((iguais ?? []) as FornecedorAchado[])[0];
  if (igual) return { achado: igual, novo: nome };
  const { data: filiais } = await supabase.from("fornecedor").select("id, nome, uf").ilike("nome", `${padrao}%`).eq("uf", uf).limit(1);
  const filial = ((filiais ?? []) as FornecedorAchado[])[0];
  if (filial) return { achado: filial, novo: nome };
  // Sem cadastro na UF: cria com o mesmo nome (as listas já mostram a UF ao lado: "FUJIOKA (DF)").
  return { achado: null, novo: nome };
}

/** Rascunho de prévia (sem gravar) para o motor calcular o preço do sistema. */
function rascunhoPrevia(r: Resolvido, margem: number | null, frete: Frete | null = null): Rascunho {
  const { linha } = r;
  const avista = Math.round(linha.custo_prazo * (1 - linha.desconto) * 1e4) / 1e4;
  return {
    ...RASCUNHO_VAZIO(r.item?.id ?? ""),
    produto_id: "previa",
    embalagem_id: "previa",
    cotacao_preco_id: "previa",
    custo_prazo: linha.custo_prazo,
    custo_avista: avista,
    cotacao_desconto: linha.desconto,
    fornecedor_uf: linha.uf,
    produto_informatica: r.produto?.informatica ?? linha.informatica,
    produto_material_escolar: r.produto?.material_escolar ?? false,
    cotacao_aliquota_interestadual: r.aliquota,
    margem,
    st_incluso: linha.st_incluso,
    pagamento: linha.desconto > 0 ? "avista" : "prazo",
    frete_compra_tipo: frete === "incluso" ? "incluso" : null,
  };
}

/** Margem a gravar no item: a da planilha, só quando difere da herdada (padrão ou licitação). */
function margemDoItem(r: Resolvido, licitacao: LicitacaoPreco, fiscal: ContextoFiscal): number | null {
  if (!r.item || r.linha.margem === null) return null;
  const herdada = calcularItem(r.item, rascunhoPrevia(r, null), licitacao, fiscal).parametros.margem;
  return Math.abs(herdada - r.linha.margem) > 1e-9 ? r.linha.margem : null;
}

/** Erros que impedem importar a linha. */
function errosDaLinha(r: Resolvido, unidades: { sigla: string; descricao: string }[]): string[] {
  const erros = [...r.linha.erros];
  if (!r.item) erros.push(`item ${r.linha.numero} não está na licitação (importe os itens antes)`);
  if (!r.produto) {
    if (r.linha.informatica === null) erros.push("produto novo sem a marcação de informática (S/N): escolha um produto existente ou marque na planilha");
    if (r.item && !unidadeDoProduto(r.item.unidade, unidades)) erros.push(`unidade "${r.item.unidade ?? ""}" não cadastrada`);
  }
  return erros;
}

export function DialogoImportarPrecificacao({
  aberto,
  licitacao,
  itens,
  rascunhos,
  fiscal,
  unidades,
  aoFechar,
  aoImportar,
}: {
  aberto: boolean;
  licitacao: LicitacaoPreco;
  itens: ItemLicitacao[];
  rascunhos: Map<string, Rascunho>;
  fiscal: ContextoFiscal;
  unidades: { sigla: string; descricao: string }[];
  aoFechar: () => void;
  aoImportar: () => void;
}) {
  const notify = useNotify();
  const celular = useCelular();
  const [arquivo, setArquivo] = useState<{ nome: string; data: Date } | null>(null);
  const [linhas, setLinhas] = useState<Resolvido[]>([]);
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [lendo, setLendo] = useState(false);
  const [gravando, setGravando] = useState(false);
  const [frete, setFrete] = useState<Frete | null>(null);

  function limpar() {
    setFrete(null);
    setArquivo(null);
    setLinhas([]);
    setErroGeral(null);
  }

  async function receberArquivo(f: File | undefined) {
    if (!f) return;
    setLendo(true);
    limpar();
    try {
      const planilha = await lerPlanilha(f);
      const lida = lerPrecificacaoPlanilha(planilha.abas);
      if (lida.erro) {
        setErroGeral(lida.erro);
        return;
      }
      const resolvidas = await Promise.all(
        lida.linhas.map(async (linha): Promise<Resolvido> => {
          const item = itens.find((i) => i.numero === linha.numero && !i.lote) ?? itens.find((i) => i.numero === linha.numero) ?? null;
          const [{ produto, achadoPor, sugestoes }, { achado: fornecedor, novo: fornecedorNovo }] = await Promise.all([
            acharProduto(linha),
            acharFornecedor(linha.fornecedor, linha.uf),
          ]);
          const uf = linha.uf;
          const aliquota = uf === fiscal.ufEmpresa ? null : (fiscal.interestadual[uf] ?? null);
          // Item que já tem produto escolhido só entra se o usuário marcar (a importação troca a escolha).
          const jaPrecificado = Boolean(item && rascunhos.get(item.id)?.produto_id);
          return { linha, item, produto, achadoPor, sugestoes, fornecedor, fornecedorNovo, aliquota, incluir: !jaPrecificado };
        }),
      );
      setArquivo({ nome: planilha.nome, data: new Date(f.lastModified) });
      setLinhas(resolvidas);
      if (resolvidas.length === 0) setErroGeral("Nenhum item com compra preenchida na aba COTAÇÃO.");
    } catch (e) {
      notify(`Não foi possível ler a planilha: ${(e as Error).message}`, { type: "error" });
    } finally {
      setLendo(false);
    }
  }

  const mudar = (indice: number, patch: Partial<Resolvido>) => setLinhas((ls) => ls.map((l, i) => (i === indice ? { ...l, ...patch } : l)));

  const previas = useMemo(
    () =>
      linhas.map((r) => {
        const erros = errosDaLinha(r, unidades);
        const margem = margemDoItem(r, licitacao, fiscal);
        const calculo = r.item && r.linha.erros.length === 0 ? calcularItem(r.item, rascunhoPrevia(r, margem, frete), licitacao, fiscal) : null;
        return { erros, margem, calculo };
      }),
    [linhas, unidades, licitacao, fiscal, frete],
  );
  const prontas = linhas.filter((r, i) => r.incluir && previas[i]?.erros.length === 0);

  async function gravar() {
    if (!arquivo || prontas.length === 0 || !frete) return;
    setGravando(true);
    // Cadastros criados nesta importação (o mesmo fornecedor aparece em vários itens).
    const fornecedores = new Map<string, string>();
    const fabricantes = new Map<string, string | null>();
    let feitos = 0;
    try {
      for (const r of prontas) {
        const item = r.item;
        if (!item) continue;
        const { linha } = r;
        const margem = margemDoItem(r, licitacao, fiscal);

        // Fornecedor.
        const chaveFornecedor = `${normalizarBusca(linha.fornecedor)}#${linha.uf}`;
        let fornecedorId = r.fornecedor?.id ?? fornecedores.get(chaveFornecedor);
        if (!fornecedorId) {
          const { data, error } = await supabase.from("fornecedor").insert({ nome: r.fornecedorNovo, uf: linha.uf }).select("id").single();
          if (error) throw new Error(`Fornecedor ${r.fornecedorNovo}: ${error.message}`);
          fornecedorId = (data as { id: string }).id;
          fornecedores.set(chaveFornecedor, fornecedorId);
        }

        // Produto e embalagem.
        let produtoId = r.produto?.id ?? null;
        let embalagemId: string | null = null;
        if (!produtoId) {
          let fabricanteId: string | null = null;
          if (linha.fabricante) {
            const chave = normalizarBusca(linha.fabricante);
            if (fabricantes.has(chave)) fabricanteId = fabricantes.get(chave) ?? null;
            else {
              const { data: achado } = await supabase.from("fabricante").select("id").ilike("nome", semCuringa(linha.fabricante)).limit(1);
              fabricanteId = ((achado ?? []) as { id: string }[])[0]?.id ?? null;
              if (!fabricanteId) {
                const { data, error } = await supabase.from("fabricante").insert({ nome: linha.fabricante }).select("id").single();
                if (error) throw new Error(`Fabricante ${linha.fabricante}: ${error.message}`);
                fabricanteId = (data as { id: string }).id;
              }
              fabricantes.set(chave, fabricanteId);
            }
          }
          const { data, error } = await supabase.rpc("salvar_produto", {
            dados: {
              descricao: linha.descricao,
              fabricante_id: fabricanteId,
              informatica: linha.informatica,
              unidade_base: unidadeDoProduto(item.unidade, unidades),
              url: linha.url,
              embalagens: [],
            },
          });
          if (error) throw new Error(`Produto do item ${linha.numero}: ${error.message}`);
          produtoId = data as string;
          const { data: base, error: erroBase } = await supabase.from("produto_embalagem").select("id").eq("produto_id", produtoId).eq("base", true).single();
          if (erroBase) throw new Error(erroBase.message);
          embalagemId = (base as { id: string }).id;
          // O COD da BASE DE DADOS fica no produto: a importação da BASE liga a este produto em vez de duplicar (M3).
          if (linha.cod) {
            const { error: erroCod } = await supabase.rpc("definir_codigo_legado", { produto_id: produtoId, codigo: linha.cod });
            if (erroCod) throw new Error(`COD ${linha.cod}: ${erroCod.message}`);
          }
        } else {
          const { data } = await supabase.from("produto_embalagem").select("id, unidade, descricao, fator_base, base").eq("produto_id", produtoId).eq("ativo", true);
          embalagemId = embalagemDaUnidade((data ?? []) as Embalagem[], item.unidade, unidades)?.id ?? null;
          if (!embalagemId) throw new Error(`Produto do item ${linha.numero} sem embalagem ativa.`);
        }

        // Cotação com a data do arquivo.
        const { data: cotacaoId, error: erroCotacao } = await supabase.rpc("registrar_cotacao_importada", {
          dados: {
            produto_id: produtoId,
            fornecedor_id: fornecedorId,
            aliquota_interestadual: r.aliquota,
            observacao: `Importada da planilha ${arquivo.nome} (linha ${linha.linha} da aba COTAÇÃO).`,
            precos: [{ embalagem_id: embalagemId, custo_prazo: linha.custo_prazo, desconto: linha.desconto }],
          },
          data_cotacao: arquivo.data.toISOString(),
        });
        if (erroCotacao) throw new Error(`Cotação do item ${linha.numero}: ${erroCotacao.message}`);
        const { data: preco, error: erroPreco } = await supabase.from("cotacao_preco").select("id").eq("cotacao_id", cotacaoId as string).single();
        if (erroPreco) throw new Error(erroPreco.message);

        await gravarRascunho(item.id, {
          produto_id: produtoId,
          embalagem_id: embalagemId,
          cotacao_preco_id: (preco as { id: string }).id,
          margem,
          st_incluso: linha.st_incluso,
          pagamento: linha.desconto > 0 ? "avista" : "prazo",
          ...(frete === "incluso" ? { frete_compra_tipo: "incluso" as const } : {}),
        });
        feitos += 1;
      }
      notify(`${feitos} item(ns) precificado(s) pela planilha. Confira os preços antes de finalizar.`, { type: "success" });
      limpar();
      aoImportar();
      aoFechar();
    } catch (e) {
      notify(`${feitos > 0 ? `${feitos} item(ns) importado(s); ` : ""}parou no erro: ${(e as Error).message}`, { type: "error" });
      if (feitos > 0) aoImportar();
    } finally {
      setGravando(false);
    }
  }

  return (
    <Dialog open={aberto} onClose={gravando ? undefined : aoFechar} fullWidth maxWidth="xl" fullScreen={celular}>
      <DialogTitle>Importar precificação da planilha</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
            <Button component="label" variant={arquivo ? "outlined" : "contained"} startIcon={<TableViewIcon />} disabled={lendo || gravando}>
              {arquivo ? "Trocar planilha" : "Escolher planilha"}
              <input type="file" hidden accept=".xlsx,.xls,.ods" onChange={(e) => void receberArquivo(e.target.files?.[0])} />
            </Button>
            {lendo && <Typography variant="body2">Lendo a planilha e procurando produtos e fornecedores…</Typography>}
            {arquivo && !lendo && (
              <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                {arquivo.nome}
              </Typography>
            )}
          </Stack>
          {!arquivo && !lendo && !erroGeral && (
            <Alert severity="info">
              Use a planilha de preço da licitação: o sistema lê as abas COTAÇÃO e PREÇO SINTÉTICO. O preço é recalculado com os parâmetros vigentes; da
              planilha vêm o produto, o fornecedor, o custo e a margem.
            </Alert>
          )}
          {erroGeral && <Alert severity="warning">{erroGeral}</Alert>}

          {arquivo && linhas.length > 0 && (
            <>
              <Typography variant="body2" color="text.secondary">
                {linhas.length} {linhas.length === 1 ? "item com compra" : "itens com compra"} na planilha. As cotações entram com a data do arquivo (
                {formatarData(arquivo.data.toISOString())}). Produto e fornecedor marcados "novo" são cadastrados na importação.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
                <Typography variant="body2">Frete do produto (a planilha não informa):</Typography>
                <ToggleButtonGroup exclusive size="small" color="primary" value={frete} onChange={(_e, v: Frete | null) => v && setFrete(v)} sx={{ flexWrap: "wrap" }}>
                  <ToggleButton value="incluso">Incluso no preço do fornecedor (CIF)</ToggleButton>
                  <ToggleButton value="depois">Informar depois, item a item</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
              <TableContainer sx={{ maxHeight: 520, overflow: "auto" }}>
                <Table size="small" stickyHeader sx={{ minWidth: 1100 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>Item</TableCell>
                      <TableCell sx={{ minWidth: 300 }}>Produto</TableCell>
                      <TableCell>Fornecedor</TableCell>
                      <TableCell align="right">Custo</TableCell>
                      <TableCell>ICMS da NF</TableCell>
                      <TableCell align="right">Margem</TableCell>
                      <TableCell align="right">Planilha</TableCell>
                      <TableCell align="right">Sistema</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {linhas.map((r, indice) => {
                      const previa = previas[indice];
                      const erros = previa?.erros ?? [];
                      const preco = previa?.calculo?.resultado?.preco_unit ?? null;
                      const diferenca = preco !== null && r.linha.preco_planilha !== null ? preco - Math.round(r.linha.preco_planilha * 100) / 100 : null;
                      const foraDoEstado = r.linha.uf !== fiscal.ufEmpresa;
                      return (
                        <TableRow key={r.linha.linha} sx={{ verticalAlign: "top", opacity: r.incluir ? 1 : 0.6 }}>
                          <TableCell padding="checkbox">
                            <Checkbox checked={r.incluir} disabled={erros.length > 0} onChange={(e) => mudar(indice, { incluir: e.target.checked })} />
                          </TableCell>
                          <TableCell>{r.linha.numero}</TableCell>
                          <TableCell>
                            <TextField
                              select
                              size="small"
                              fullWidth
                              value={r.produto?.id ?? ""}
                              slotProps={{
                                select: {
                                  displayEmpty: true,
                                  renderValue: () => (
                                    <Typography variant="body2" sx={{ whiteSpace: "normal" }}>
                                      {r.produto ? `${r.produto.codigo} — ${r.produto.descricao}` : `Cadastrar novo: ${r.linha.descricao}`}
                                    </Typography>
                                  ),
                                },
                              }}
                              onChange={(e) => {
                                const escolhido = [r.produto, ...r.sugestoes].find((p) => p?.id === e.target.value) ?? null;
                                mudar(indice, { produto: escolhido, achadoPor: null });
                              }}
                              helperText={
                                r.produto
                                  ? r.achadoPor === "cod"
                                    ? `Achado pelo COD ${r.linha.cod}`
                                    : r.achadoPor === "descricao"
                                      ? "Achado pela descrição"
                                      : "Escolhido entre os parecidos"
                                  : `Novo: ${[r.linha.fabricante, r.linha.cod ? `COD ${r.linha.cod}` : null].filter(Boolean).join(" · ") || "sem fabricante"}`
                              }
                            >
                              <MenuItem value="">
                                <Typography variant="body2" sx={{ whiteSpace: "normal" }}>
                                  Cadastrar novo: {r.linha.descricao}
                                </Typography>
                              </MenuItem>
                              {[r.produto, ...r.sugestoes]
                                .filter((p, i, todos): p is ProdutoAchado => Boolean(p) && todos.findIndex((q) => q?.id === p?.id) === i)
                                .map((p) => (
                                  <MenuItem key={p.id} value={p.id}>
                                    <Typography variant="body2" sx={{ whiteSpace: "normal" }}>
                                      {p.codigo} — {p.descricao}
                                    </Typography>
                                  </MenuItem>
                                ))}
                            </TextField>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {r.fornecedor?.nome ?? r.fornecedorNovo} · {r.linha.uf}
                            </Typography>
                            {!r.fornecedor && r.linha.fornecedor && <Chip size="small" variant="outlined" color="secondary" label="novo" sx={{ mt: 0.5 }} />}
                          </TableCell>
                          <TableCell align="right">
                            {formatarMoeda(r.linha.custo_prazo)}
                            {r.linha.desconto > 0 && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                à vista {formatarMoeda(r.linha.custo_prazo * (1 - r.linha.desconto))}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {foraDoEstado ? (
                              <TextField
                                select
                                size="small"
                                value={r.aliquota ?? ""}
                                onChange={(e) => mudar(indice, { aliquota: e.target.value === "" ? null : Number(e.target.value) })}
                                helperText="Pela matriz; confira na NF"
                                sx={{ minWidth: 120 }}
                              >
                                <MenuItem value="0.04">4% (importado)</MenuItem>
                                <MenuItem value="0.07">7%</MenuItem>
                                <MenuItem value="0.12">12%</MenuItem>
                              </TextField>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                Compra dentro do estado
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {r.linha.margem === null ? "—" : formatarPercentual(r.linha.margem)}
                            {previa?.margem === null && r.linha.margem !== null && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                igual à herdada
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {formatarMoeda(r.linha.preco_planilha === null ? null : Math.round(r.linha.preco_planilha * 100) / 100)}
                          </TableCell>
                          <TableCell align="right">
                            {formatarMoeda(preco)}
                            {diferenca !== null && Math.abs(diferenca) >= 0.005 && (
                              <Typography variant="caption" display="block" color={diferenca > 0 ? "warning.main" : "success.main"}>
                                {diferenca > 0 ? "+" : "−"}
                                {formatarMoeda(Math.abs(diferenca))}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <AvisosDasLinhas linhas={linhas} previas={previas} rascunhos={rascunhos} />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={aoFechar} disabled={gravando}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={() => void gravar()} disabled={gravando || prontas.length === 0 || !frete}>
          {gravando ? "Importando…" : prontas.length === 1 ? "Importar 1 item" : `Importar ${prontas.length} itens`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Erros e avisos por item, abaixo da tabela (a tabela fica limpa para conferir os preços). */
function AvisosDasLinhas({
  linhas,
  previas,
  rascunhos,
}: {
  linhas: Resolvido[];
  previas: { erros: string[]; calculo: ReturnType<typeof calcularItem> | null }[];
  rascunhos: Map<string, Rascunho>;
}) {
  const itens = linhas
    .map((r, i) => {
      const previa = previas[i];
      const jaPrecificado = Boolean(r.item && rascunhos.get(r.item.id)?.produto_id);
      const avisos = [
        ...r.linha.avisos,
        ...(jaPrecificado ? ["já tem produto escolhido: marque o item para trocar pela planilha"] : []),
        ...(r.produto && r.linha.informatica !== null && r.produto.informatica !== null && r.produto.informatica !== r.linha.informatica
          ? [`informática: no sistema "${r.produto.informatica ? "sim" : "não"}", na planilha "${r.linha.informatica ? "sim" : "não"}" (vale o sistema)`]
          : []),
        ...(previa?.calculo?.pendencia ? [previa.calculo.pendencia] : []),
      ];
      return { r, erros: previa?.erros ?? [], avisos };
    })
    .filter((x) => x.erros.length > 0 || x.avisos.length > 0);
  if (itens.length === 0) return null;
  return (
    <Stack spacing={1}>
      {itens.map(({ r, erros, avisos }) => (
        <Alert key={r.linha.linha} severity={erros.length ? "error" : "warning"} sx={{ py: 0 }}>
          <strong>Item {r.linha.numero}</strong>
          {erros.length > 0 && ` — não será importado: ${erros.join("; ")}`}
          {avisos.length > 0 && ` — ${avisos.join("; ")}`}
        </Alert>
      ))}
    </Stack>
  );
}
